import { useMemo, useState } from "react";
import { resolveReviewStatus } from "@hooks/useJobResults";
import type { UseJobResultsState } from "@hooks/useJobResults.types";
import {
  MappingStatus,
  ReviewStatus,
} from "@models/job";
import {
  ConfidenceBand,
  MappingResultKind,
} from "@models/mappingResult";
import type { MappingResultRow } from "@models/mappingResult";
import type { ReviewStatusFilter } from "@utils/jobResultsHelpers";

export interface UseResultsFilterResult {
  statusFilter: ReviewStatusFilter;
  setStatusFilter: (filter: ReviewStatusFilter) => void;
  priorityOnly: boolean;
  setPriorityOnly: (value: boolean) => void;
  /** Show only rows the pipeline found no catalogue entry for (wd-mapping). */
  missingOnly: boolean;
  setMissingOnly: (value: boolean) => void;
  statusCounts: Record<ReviewStatusFilter, number>;
  priorityCount: number;
  missingCount: number;
  filteredRows: MappingResultRow[];
}

/**
 * Derives the visible row set from the active status / priority filters and
 * computes the counts shown in the filter chips.
 *
 * There is no longer an "outside template" filter. It hid rows whose target
 * fell outside the active QA template, but the pipeline now hard-filters those
 * hits away before a row is ever written, so the filter had nothing left to
 * match on — and once its predicate moved to the cross-service `mapping_status`
 * it started hiding wd-mapping's `no_item_found` rows instead, which are the
 * rows a wd reviewer most needs to see.
 *
 * Every count is over the rows loaded so far, not the whole job — the result
 * list is paginated.
 */
export const useResultsFilter = (
  results: UseJobResultsState,
): UseResultsFilterResult => {
  const [statusFilter, setStatusFilter] = useState<ReviewStatusFilter>("all");
  const [priorityOnly, setPriorityOnlyState] = useState(false);
  const [missingOnly, setMissingOnlyState] = useState(false);

  // The two toggles exclude each other rather than intersect: a catalogue miss
  // deliberately carries NO confidence band (the miss IS the warning, so the
  // backend leaves the band off to keep one signal per row), which makes their
  // intersection always empty. Letting both be on would hand the reviewer an
  // empty table and no hint why.
  const setPriorityOnly = (value: boolean): void => {
    setPriorityOnlyState(value);
    if (value) setMissingOnlyState(false);
  };
  const setMissingOnly = (value: boolean): void => {
    setMissingOnlyState(value);
    if (value) setPriorityOnlyState(false);
  };

  const statusCounts = useMemo(() => {
    const counts: Record<ReviewStatusFilter, number> = {
      all: results.rows.length,
      [ReviewStatus.Pending]: 0,
      [ReviewStatus.Accepted]: 0,
      [ReviewStatus.Rejected]: 0,
      [ReviewStatus.Corrected]: 0,
      [ReviewStatus.Failed]: 0,
    };
    for (const row of results.rows) {
      const effective = resolveReviewStatus(
        row,
        results.edits.get(row.rowIndex),
      );
      counts[effective] += 1;
    }
    return counts;
  }, [results.rows, results.edits]);

  // Priority reads `confidenceBand`, NOT `mappingStatus === NeedsReview`.
  // Confidence is its OWN axis, and reading it off `mappingStatus` alone would
  // lose the case this chip exists for: that field is single-valued and
  // reports the most severe finding, so a row that is both low-confidence AND
  // a catalogue miss comes back as the miss and silently drops out.
  //
  // OE therefore still ships a graded `confidenceBand` on the target, and it
  // is read first. TM has no graded band — nothing finer than "low" is
  // measured there — so its low-confidence rows arrive as `needs_review` and
  // that is what the chip reads for them.
  const isPriority = (row: MappingResultRow) =>
    row.kind === MappingResultKind.Oe
      ? row.target?.confidenceBand === ConfidenceBand.Low
      : row.mappingStatus === MappingStatus.NeedsReview;

  const priorityCount = useMemo(
    () => results.rows.filter(isPriority).length,
    [results.rows],
  );

  // The catalogue-miss chip: the rows a reviewer has to hand over rather than
  // fix, since there is no entry to pick. Read off `mappingStatus` — the
  // PIPELINE's verdict, so the chip keeps listing a row the reviewer has since
  // corrected, which is what a "what did the run conclude" filter should do.
  // Whether a given row is STILL a miss is a per-row question the table answers
  // from its effective match (see `isSuggestion`).
  //
  // Filtered client-side like every other chip here: the results hook already
  // walks the cursor to exhaustion, so every row is in memory. The API's
  // `?mapping_status=` filter would mean a second paged walk whose pages come
  // back short or empty while more rows remain — a worse trade for a set we
  // already hold.
  const isMissing = (row: MappingResultRow) =>
    row.mappingStatus === MappingStatus.NoItemFound;

  const missingCount = useMemo(
    () => results.rows.filter(isMissing).length,
    [results.rows],
  );

  const filteredRows = useMemo(() => {
    return results.rows.filter((row) => {
      if (priorityOnly && !isPriority(row)) return false;
      if (missingOnly && !isMissing(row)) return false;
      if (
        statusFilter !== "all" &&
        resolveReviewStatus(row, results.edits.get(row.rowIndex)) !==
          statusFilter
      ) {
        return false;
      }
      return true;
    });
  }, [results.rows, results.edits, statusFilter, priorityOnly, missingOnly]);

  return {
    statusFilter,
    setStatusFilter,
    priorityOnly,
    setPriorityOnly,
    missingOnly,
    setMissingOnly,
    statusCounts,
    priorityCount,
    missingCount,
    filteredRows,
  };
};
